import { effect, shallowRef, stop } from '@vue/reactivity'
import { evaluateInScope } from '../component/utils'
import { parseDirectives } from '../component/init'
import { cleanupComponentsIn, reviveComponentsIn } from '../component/reactive'

export default {

    signature: 'qv-if',

    handle(node, scope) {

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return null
        }

        if (!node.hasAttribute(this.signature)) {
            return
        }

        const expression = node.getAttribute(this.signature)

        node.removeAttribute('qv-if')

        if (node.tagName === 'TEMPLATE') {
            return handleTemplateIf(node, expression, scope)
        }

        return handleElementIf(node, expression, scope)

    },

}

function runCleanups(callbacks) {

    for (const callback of callbacks) {
        callback()
    }

    callbacks.length = 0

}

function handleElementIf(node, expression, scope) {

    const comment = document.createComment(`qv-if for: ${ expression }`)
    const parent = node.parentNode
    const cleanupCallbacks = []

    let isMounted = true
    let isBound = false

    /*
     * Expose this scope to nested <script arbor :prop> tags. We use a holder
     * (rather than the scope directly) so the same reference can be reused
     * across remounts.
     */
    if (!node._qvScopeHolder) {
        node._qvScopeHolder = shallowRef(scope)
    } else {
        node._qvScopeHolder.value = scope
    }

    const runner = effect(() => {

        const condition = evaluateInScope(expression, scope)

        if (condition) {

            if (!isMounted) {

                parent.insertBefore(node, comment)

                if (comment.parentNode) {
                    comment.parentNode.removeChild(comment)
                }

                isMounted = true
                reviveComponentsIn(node)

            }

            if (!isBound) {

                cleanupCallbacks.push(...parseDirectives(node, scope))
                isBound = true

            }

        } else {

            if (isMounted && node.parentNode === parent) {

                cleanupComponentsIn(node)
                parent.insertBefore(comment, node)
                parent.removeChild(node)
                isMounted = false
                runCleanups(cleanupCallbacks)
                isBound = false

            }

        }

    })

    return () => {

        if (isMounted) {
            cleanupComponentsIn(node)
        }

        runCleanups(cleanupCallbacks)
        stop(runner)

        if (!isMounted && comment.parentNode) {
            comment.parentNode.removeChild(comment)
        }

    }

}

function handleTemplateIf(template, expression, scope) {

    const cleanupCallbacks = []

    let clonedNodes = []

    const runner = effect(() => {

        const condition = evaluateInScope(expression, scope)

        if (condition) {

            if (clonedNodes.length === 0) {

                const fragment = template.content.cloneNode(true)

                clonedNodes = Array.from(fragment.childNodes)
                template.after(fragment)

                for (const clonedNode of clonedNodes) {

                    if (clonedNode.nodeType !== Node.ELEMENT_NODE) {
                        continue
                    }

                    clonedNode._qvScopeHolder = shallowRef(scope)
                    cleanupCallbacks.push(...parseDirectives(clonedNode, scope))
                    reviveComponentsIn(clonedNode)

                }

            }

        } else {

            if (clonedNodes.length > 0) {

                for (const clonedNode of clonedNodes) {
                    cleanupComponentsIn(clonedNode)
                }

                runCleanups(cleanupCallbacks)

                for (const clonedNode of clonedNodes) {
                    clonedNode.remove()
                }

                clonedNodes = []

            }

        }

    })

    return () => {

        for (const clonedNode of clonedNodes) {
            cleanupComponentsIn(clonedNode)
        }

        runCleanups(cleanupCallbacks)
        stop(runner)

        for (const clonedNode of clonedNodes) {
            clonedNode.remove()
        }

    }

}
